import React, { useMemo, useState } from "react";

// Lightweight modal for configuring an Assignment Group
// Props:
// - isOpen: boolean
// - onClose: () => void
// - onSubmit: ({ title, assignmentDocId, referenceDocIds, notes }) => void
// - documents: [{ id, name }]
export default function AssignmentSetupModal({ isOpen, onClose, onSubmit, documents = [] }) {
  const [title, setTitle] = useState("");
  const [assignmentDocId, setAssignmentDocId] = useState("");
  const [referenceDocIds, setReferenceDocIds] = useState([]);
  const [notes, setNotes] = useState("");

  const handleToggleReference = (id) => {
    setReferenceDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const canSubmit = useMemo(() => title.trim().length > 0, [title]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#111827', // gray-900
        color: '#e5e7eb', // gray-200
        width: 'min(680px, 92vw)',
        borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.5)', padding: 16,
        border: '1px solid #1f2937' // gray-800
      }}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
          <h3 style={{margin: 0, fontSize: 18}}>Set up Assignment</h3>
          <button onClick={onClose} aria-label="Close" style={{border: '1px solid #374151', background: '#1f2937', color: '#e5e7eb', borderRadius: 8, padding: '6px 10px'}}>Close</button>
        </div>
        <div style={{display: 'grid', gap: 12}}>
          <label style={{display: 'grid', gap: 6}}>
            <span style={{fontSize: 12, color: '#9ca3af'}}>Title</span>
            <input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g., Essay on Climate Policy" style={{border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb', borderRadius: 8, padding: '8px 10px'}} />
          </label>

          <label style={{display: 'grid', gap: 6}}>
            <span style={{fontSize: 12, color: '#9ca3af'}}>Assignment Document (optional)</span>
            <select value={assignmentDocId} onChange={(e)=>setAssignmentDocId(e.target.value)} style={{border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb', borderRadius: 8, padding: '8px 10px'}}>
              <option value="">None</option>
              {documents.map(doc => (
                <option key={doc.id} value={doc.id}>{doc.name}</option>
              ))}
            </select>
          </label>

          <div style={{display: 'grid', gap: 6}}>
            <span style={{fontSize: 12, color: '#9ca3af'}}>Reference Documents</span>
            <div style={{
              border: '1px solid #374151', borderRadius: 8, padding: 8,
              maxHeight: 200, overflow: 'auto', display: 'grid', gap: 6,
              background: '#0b1220'
            }}>
              {documents.length === 0 && (
                <div style={{fontSize: 12, color: '#9ca3af'}}>No documents uploaded yet.</div>
              )}
              {documents.map(doc => (
                <label key={doc.id} style={{display: 'flex', alignItems: 'center', gap: 8}}>
                  <input
                    type="checkbox"
                    checked={referenceDocIds.includes(doc.id)}
                    onChange={() => handleToggleReference(doc.id)}
                  />
                  <span style={{fontSize: 13}}>{doc.name}</span>
                </label>
              ))}
            </div>
          </div>

          <label style={{display: 'grid', gap: 6}}>
            <span style={{fontSize: 12, color: '#9ca3af'}}>Notes / Instructions</span>
            <textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={4} placeholder="Anything specific to highlight?" style={{border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb', borderRadius: 8, padding: '8px 10px'}} />
          </label>
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12}}>
          <button onClick={onClose} style={{border: '1px solid #374151', background: '#1f2937', color: '#e5e7eb', borderRadius: 8, padding: '8px 12px'}}>Cancel</button>
          <button
            onClick={() => onSubmit({ title: title.trim(), assignmentDocId: assignmentDocId || null, referenceDocIds, notes })}
            disabled={!canSubmit}
            style={{
              border: '1px solid #1d4ed8', background: '#2563eb', color: '#fff',
              opacity: canSubmit ? 1 : 0.6, cursor: canSubmit ? 'pointer' : 'not-allowed',
              borderRadius: 8, padding: '8px 12px'
            }}
          >
            Create Group
          </button>
        </div>
      </div>
    </div>
  );
}
